import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getApiUrl } from "../utils/api";

const TrackPage = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("Received");
  const [statusMessage, setStatusMessage] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const adminToken = localStorage.getItem("adminToken");
    if (adminToken) {
      setIsAdmin(true);
    }

    fetch(getApiUrl(`/track/${encodeURIComponent(id)}`))
      .then((res) => res.json())
      .then((res) => {
        if (res.success && res.data) {
          setData(res.data);
          setSelectedStatus(res.data.status || "Received");
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [id]);

  const handleStatusUpdate = async () => {
    if (!isAdmin) return;
    setUpdating(true);
    setStatusMessage("");

    try {
      const token = localStorage.getItem("adminToken");
      const res = await fetch(getApiUrl(`/admin/contacts/${encodeURIComponent(id)}/status`), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: selectedStatus })
      });

      const result = await res.json();
      if (result.success && result.data) {
        setData(result.data);
        setStatusMessage(`✅ Status updated to "${selectedStatus}" in MongoDB!`);
      } else {
        setStatusMessage(`❌ Error: ${result.message || "Failed to update status"}`);
      }
    } catch (err) {
      console.error("Failed to update status:", err);
      setStatusMessage("❌ Server error while updating status.");
    } finally {
      setUpdating(false);
    }
  };

  // 🔄 Loading
  if (loading) {
    return (
      <div style={styles.center}>
        <div style={styles.card}>
          <h2 style={{ color: "#38bdf8", textAlign: "center" }}>Loading Request Details...</h2>
        </div>
      </div>
    );
  }

  // ❌ Not found
  if (!data) {
    return (
      <div style={styles.center}>
        <div style={styles.card}>
          <h2 style={{ color: "#ef4444", textAlign: "center" }}>❌ Tracking ID Not Found</h2>
          <p style={{ textAlign: "center", color: "#9ca3af" }}>No record matching tracking ID "{id}"</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <img
            src="https://res.cloudinary.com/dxbwhvybv/image/upload/v1774941428/Praveen_Kumar_hp4ucf.png"
            width={75}
            alt="Praveen Logo"
            style={{
              borderRadius: "50%",
              border: "2px solid rgba(56,189,248,0.5)",
              boxShadow: "0 0 15px rgba(56,189,248,0.3)"
            }}
          />
        </div>

        <h1 style={styles.heading}>🔍 Request Details & Status</h1>

        <p style={styles.sub}>
          Tracking ID: <b style={{ color: "#00eaff" }}>{data.trackingId || id}</b>
        </p>

        {/* Current Status Badge */}
        <div style={styles.statusBox}>
          <span style={getStatusStyle(data.status)}>
            {data.status}
          </span>
        </div>

        {/* ADMIN STATUS UPDATE CONTROL PANEL */}
        {isAdmin && (
          <div style={styles.adminPanel}>
            <h3 style={styles.adminPanelTitle}>👑 Admin Status Control (MongoDB)</h3>
            
            {statusMessage && <div style={styles.statusAlert}>{statusMessage}</div>}

            <div style={styles.adminControlRow}>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                style={styles.statusSelect}
              >
                <option value="Received">Received</option>
                <option value="In Progress">In Progress</option>
                <option value="Resolved">Resolved</option>
                <option value="Closed">Closed</option>
              </select>

              <button
                onClick={handleStatusUpdate}
                disabled={updating}
                style={styles.updateBtn}
              >
                {updating ? "Saving..." : "Update Status 💾"}
              </button>
            </div>
          </div>
        )}

        {/* COMPLETE USER INFO */}
        <div style={styles.infoGrid}>
          <div style={styles.infoRow}>
            <span style={styles.label}>Full Name:</span>
            <span style={styles.value}>{data.firstName} {data.lastName}</span>
          </div>

          <div style={styles.infoRow}>
            <span style={styles.label}>Email Address:</span>
            <span style={styles.value}>{data.email}</span>
          </div>

          <div style={styles.infoRow}>
            <span style={styles.label}>Phone Number:</span>
            <span style={styles.value}>{data.countryCode ? `${data.countryCode} ` : ""}{data.mobile || "N/A"}</span>
          </div>

          <div style={styles.infoRow}>
            <span style={styles.label}>Location:</span>
            <span style={styles.value}>{data.city}, {data.state}, {data.country}</span>
          </div>

          <div style={styles.infoRow}>
            <span style={styles.label}>Submitted On:</span>
            <span style={styles.value}>{data.createdAt ? new Date(data.createdAt).toLocaleString() : "N/A"}</span>
          </div>
        </div>

        {/* MESSAGE CONTENT */}
        <div style={{ marginTop: 20 }}>
          <span style={styles.label}>Message Content:</span>
          <div style={styles.messageBox}>{data.message}</div>
        </div>
      </div>
    </div>
  );
};

const getStatusStyle = (status) => {
  let bg = "linear-gradient(90deg,#00eaff,#007cf0)";
  if (status === "In Progress") bg = "linear-gradient(90deg,#f59e0b,#d97706)";
  if (status === "Resolved") bg = "linear-gradient(90deg,#10b981,#059669)";
  if (status === "Closed") bg = "linear-gradient(90deg,#a855f7,#7e22ce)";

  return {
    background: bg,
    padding: "8px 20px",
    borderRadius: "20px",
    fontSize: "14px",
    fontWeight: "bold",
    color: "#fff",
    display: "inline-block",
    boxShadow: "0 4px 15px rgba(0,0,0,0.3)"
  };
};

// 🎨 Glassmorphic Styles
const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "radial-gradient(circle at top left, #0f172a, #090d16)",
    padding: "40px 20px",
    fontFamily: "'Segoe UI', sans-serif"
  },
  center: {
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "#090d16"
  },
  card: {
    width: "100%",
    maxWidth: "600px",
    padding: "32px",
    borderRadius: "24px",
    background: "rgba(255, 255, 255, 0.05)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.12)",
    boxShadow: "0 20px 50px rgba(0, 0, 0, 0.5)",
    color: "#fff",
    boxSizing: "border-box"
  },
  heading: {
    textAlign: "center",
    marginBottom: "6px",
    fontSize: "22px",
    color: "#f8fafc"
  },
  sub: {
    textAlign: "center",
    color: "#94a3b8",
    marginBottom: "20px",
    fontSize: "14px"
  },
  statusBox: {
    textAlign: "center",
    marginBottom: "24px"
  },
  adminPanel: {
    background: "rgba(56, 189, 248, 0.08)",
    border: "1px solid rgba(56, 189, 248, 0.25)",
    borderRadius: "16px",
    padding: "16px",
    marginBottom: "24px"
  },
  adminPanelTitle: {
    fontSize: "14px",
    color: "#38bdf8",
    margin: "0 0 12px 0",
    fontWeight: "700"
  },
  statusAlert: {
    padding: "8px 12px",
    borderRadius: "8px",
    background: "rgba(16, 185, 129, 0.2)",
    border: "1px solid rgba(16, 185, 129, 0.4)",
    color: "#34d399",
    fontSize: "12px",
    marginBottom: "12px"
  },
  adminControlRow: {
    display: "flex",
    gap: "12px"
  },
  statusSelect: {
    flex: 1,
    padding: "10px 14px",
    borderRadius: "10px",
    background: "rgba(15, 23, 42, 0.8)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    color: "#ffffff",
    fontSize: "14px",
    outline: "none"
  },
  updateBtn: {
    padding: "10px 20px",
    background: "linear-gradient(135deg, #38bdf8, #0284c7)",
    border: "none",
    borderRadius: "10px",
    color: "#ffffff",
    fontWeight: "700",
    cursor: "pointer",
    fontSize: "14px",
    boxShadow: "0 4px 12px rgba(56, 189, 248, 0.3)"
  },
  infoGrid: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    background: "rgba(255, 255, 255, 0.03)",
    padding: "18px",
    borderRadius: "14px",
    border: "1px solid rgba(255, 255, 255, 0.06)"
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid rgba(255, 255, 255, 0.04)",
    paddingBottom: "8px"
  },
  label: {
    color: "#94a3b8",
    fontSize: "13px",
    fontWeight: "600"
  },
  value: {
    color: "#f1f5f9",
    fontWeight: "600",
    fontSize: "14px"
  },
  messageBox: {
    background: "rgba(255, 255, 255, 0.05)",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    padding: "16px",
    borderRadius: "12px",
    fontSize: "14px",
    color: "#e2e8f0",
    lineHeight: "1.6",
    marginTop: "8px"
  }
};

export default TrackPage;